import { z } from "zod";

interface ChainBase {
	label: string;
	icon: string;
	iconDark: string;
	txExplorerUrl?: string;
	addressExplorerUrl?: string;
}

interface Chain extends ChainBase {
	txExplorerUrl: string;
	addressExplorerUrl: string;
}

type KnownChain = Chain | ChainBase;

export const NEAR_TX_EXPLORER_URL = "https://nearblocks.io/txns/";
export const NEAR_ADDRESS_EXPLORER_URL = "https://nearblocks.io/address/";
export const NEAR_INTENTS_EXPLORER_URL = "https://pikespeak.ai/intents/";
export const PRICE_IMPROVEMENT_FEE_RECEIVER =
	"0bc39dd4082e871df81eaa28250bf403fe900320a9d9929dc1304f17df462bac";

export const CHAIN_IDS = [
	"near",
	"eth",
	"base",
	"arb",
	"btc",
	"sol",
	"ton",
	"doge",
	"xrp",
	"zec",
	"gnosis",
	"bera",
	"bsc",
	"pol",
	"tron",
	"sui",
	"op",
	"hood",
	"qtc",
	"prl",
	"avax",
	"cardano",
	"stellar",
	"aptos",
	"ltc",
	"monad",
	"xlayer",
	"starknet",
	"bch",
	"adi",
	"plasma",
	"scroll",
	"aleo",
	"movement",
	"dash",
	"abs",
	"hypercore",
	"fogo",
] as const;

export const chainsEnum = z.enum(CHAIN_IDS);

// 1Click blockchain names that differ from the explorer's chain ids.
export const CHAIN_MAPPING: ReadonlyMap<string, string> = new Map([
	["layerx", "xlayer"],
	["bitcoincash", "bch"],
]);

type ChainId = (typeof CHAIN_IDS)[number];

const knownChains = {
	near: {
		label: "Near",
		icon: "/icons/network/near.svg",
		iconDark: "/icons/network/near_dark.svg",
		txExplorerUrl: NEAR_TX_EXPLORER_URL,
		addressExplorerUrl: NEAR_ADDRESS_EXPLORER_URL,
	},
	eth: {
		label: "Ethereum",
		icon: "/icons/network/ethereum_white.svg",
		iconDark: "/icons/network/ethereum.svg",
		txExplorerUrl: "https://etherscan.io/tx/",
		addressExplorerUrl: "https://etherscan.io/address/",
	},
	base: {
		label: "Base",
		icon: "/icons/network/base.svg",
		iconDark: "/icons/network/base.svg",
		txExplorerUrl: "https://basescan.org/tx/",
		addressExplorerUrl: "https://basescan.org/address/",
	},
	arb: {
		label: "Arbitrum",
		icon: "/icons/network/arbitrum.svg",
		iconDark: "/icons/network/arbitrum.svg",
		txExplorerUrl: "https://arbiscan.io/tx/",
		addressExplorerUrl: "https://arbiscan.io/address/",
	},
	arbitrum: {
		label: "Arbitrum",
		icon: "/icons/network/arbitrum.svg",
		iconDark: "/icons/network/arbitrum.svg",
		txExplorerUrl: "https://arbiscan.io/tx/",
		addressExplorerUrl: "https://arbiscan.io/address/",
	},
	btc: {
		label: "Bitcoin",
		icon: "/icons/network/btc.svg",
		iconDark: "/icons/network/btc.svg",
		txExplorerUrl: "https://mempool.space/tx/",
		addressExplorerUrl: "https://mempool.space/address/",
	},
	bitcoin: {
		label: "Bitcoin",
		icon: "/icons/network/btc.svg",
		iconDark: "/icons/network/btc.svg",
		txExplorerUrl: "https://mempool.space/tx/",
		addressExplorerUrl: "https://mempool.space/address/",
	},
	sol: {
		label: "Solana",
		icon: "/icons/network/solana.svg",
		iconDark: "/icons/network/solana.svg",
		txExplorerUrl: "https://orb.helius.dev/tx/",
		addressExplorerUrl: "https://orb.helius.dev/account/",
	},
	solana: {
		label: "Solana",
		icon: "/icons/network/solana.svg",
		iconDark: "/icons/network/solana.svg",
		txExplorerUrl: "https://orb.helius.dev/tx/",
		addressExplorerUrl: "https://orb.helius.dev/account/",
	},
	ton: {
		label: "TON",
		icon: "/icons/network/ton.svg",
		iconDark: "/icons/network/ton.svg",
		txExplorerUrl: "https://tonscan.org/tx/",
		addressExplorerUrl: "https://tonscan.org/address/",
	},
	doge: {
		label: "Dogecoin",
		icon: "/icons/network/dogecoin.svg",
		iconDark: "/icons/network/dogecoin.svg",
		txExplorerUrl: "https://blockchair.com/dogecoin/transaction/",
		addressExplorerUrl: "https://blockchair.com/dogecoin/address/",
	},
	dogecoin: {
		label: "Dogecoin",
		icon: "/icons/network/dogecoin.svg",
		iconDark: "/icons/network/dogecoin.svg",
		txExplorerUrl: "https://blockchair.com/dogecoin/transaction/",
		addressExplorerUrl: "https://blockchair.com/dogecoin/address/",
	},
	xrp: {
		label: "XRP Ledger",
		icon: "/icons/network/xrpledger_white.svg",
		iconDark: "/icons/network/xrpledger.svg",
		txExplorerUrl: "https://xrpscan.com/tx/",
		addressExplorerUrl: "https://xrpscan.com/account/",
	},
	xrpledger: {
		label: "XRP Ledger",
		icon: "/icons/network/xrpledger_white.svg",
		iconDark: "/icons/network/xrpledger.svg",
		txExplorerUrl: "https://xrpscan.com/tx/",
		addressExplorerUrl: "https://xrpscan.com/account/",
	},
	zec: {
		label: "Zcash",
		icon: "/icons/network/zcash.svg",
		iconDark: "/icons/network/zcash.svg",
		txExplorerUrl: "https://3xpl.com/zcash/transaction/",
		addressExplorerUrl: "https://3xpl.com/zcash/address/",
	},
	zcash: {
		label: "Zcash",
		icon: "/icons/network/zcash.svg",
		iconDark: "/icons/network/zcash.svg",
		txExplorerUrl: "https://3xpl.com/zcash/transaction/",
		addressExplorerUrl: "https://3xpl.com/zcash/address/",
	},
	gnosis: {
		label: "Gnosis",
		icon: "/icons/network/gnosis_white.svg",
		iconDark: "/icons/network/gnosis.svg",
		txExplorerUrl: "https://gnosisscan.io/tx/",
		addressExplorerUrl: "https://gnosisscan.io/address/",
	},
	bera: {
		label: "BeraChain",
		icon: "/icons/network/berachain.svg",
		iconDark: "/icons/network/berachain.svg",
		txExplorerUrl: "https://beratrail.io/tx/",
		addressExplorerUrl: "https://beratrail.io/address/",
	},
	berachain: {
		label: "BeraChain",
		icon: "/icons/network/berachain.svg",
		iconDark: "/icons/network/berachain.svg",
		txExplorerUrl: "https://beratrail.io/tx/",
		addressExplorerUrl: "https://beratrail.io/address/",
	},
	bsc: {
		label: "BNB Smart Chain",
		icon: "/icons/network/bsc.svg",
		iconDark: "/icons/network/bsc.svg",
		txExplorerUrl: "https://bscscan.com/tx/",
		addressExplorerUrl: "https://bscscan.com/address/",
	},
	pol: {
		label: "Polygon",
		icon: "/icons/network/polygon.svg",
		iconDark: "/icons/network/polygon.svg",
		txExplorerUrl: "https://polygonscan.com/tx/",
		addressExplorerUrl: "https://polygonscan.com/address/",
	},
	polygon: {
		label: "Polygon",
		icon: "/icons/network/polygon.svg",
		iconDark: "/icons/network/polygon.svg",
		txExplorerUrl: "https://polygonscan.com/tx/",
		addressExplorerUrl: "https://polygonscan.com/address/",
	},
	tron: {
		label: "Tron",
		icon: "/icons/network/tron.svg",
		iconDark: "/icons/network/tron.svg",
		txExplorerUrl: "https://tronscan.org/#/transaction/",
		addressExplorerUrl: "https://tronscan.org/#/address/",
	},
	sui: {
		label: "Sui",
		icon: "/icons/network/sui.svg",
		iconDark: "/icons/network/sui_dark.svg",
		txExplorerUrl: "https://suiscan.xyz/tx/",
		addressExplorerUrl: "https://suiscan.xyz/address/",
	},
	hood: {
		label: "Robinhood",
		icon: "/icons/network/robinhood.svg",
		iconDark: "/icons/network/robinhood.svg",
		txExplorerUrl: "https://robinhoodchain.blockscout.com/tx/",
		addressExplorerUrl: "https://robinhoodchain.blockscout.com/address/",
	},
	op: {
		label: "Optimism",
		icon: "/icons/network/optimism.svg",
		iconDark: "/icons/network/optimism_dark.svg",
		txExplorerUrl: "https://optimistic.etherscan.io/tx/",
		addressExplorerUrl: "https://optimistic.etherscan.io/address/",
	},
	optimism: {
		label: "Optimism",
		icon: "/icons/network/optimism.svg",
		iconDark: "/icons/network/optimism_dark.svg",
		txExplorerUrl: "https://optimistic.etherscan.io/tx/",
		addressExplorerUrl: "https://optimistic.etherscan.io/address/",
	},
	avax: {
		label: "Avalanche",
		icon: "/icons/network/avalanche.svg",
		iconDark: "/icons/network/avalanche.svg",
		txExplorerUrl: "https://snowtrace.io/tx/",
		addressExplorerUrl: "https://snowtrace.io/address/",
	},
	avalanche: {
		label: "Avalanche",
		icon: "/icons/network/avalanche.svg",
		iconDark: "/icons/network/avalanche.svg",
		txExplorerUrl: "https://snowtrace.io/tx/",
		addressExplorerUrl: "https://snowtrace.io/address/",
	},
	cardano: {
		label: "Cardano",
		icon: "/icons/network/cardano.svg",
		iconDark: "/icons/network/cardano.svg",
		txExplorerUrl: "https://cardanoscan.io/transaction/",
		addressExplorerUrl: "https://cardanoscan.io/address/",
	},
	stellar: {
		label: "Stellar",
		icon: "/icons/network/stellar_white.svg",
		iconDark: "/icons/network/stellar.svg",
		txExplorerUrl: "https://stellar.expert/explorer/public/tx/",
		addressExplorerUrl: "https://stellar.expert/explorer/public/account/",
	},
	aptos: {
		label: "Aptos",
		icon: "/icons/network/aptos_white.svg",
		iconDark: "/icons/network/aptos.svg",
		txExplorerUrl: "https://explorer.aptoslabs.com/txn/",
		addressExplorerUrl: "https://explorer.aptoslabs.com/account/",
	},
	turbochain: {
		label: "TurboChain",
		icon: "/icons/network/turbochain.png",
		iconDark: "/icons/network/turbochain.png",
		txExplorerUrl: "https://turbochain.io/tx/",
		addressExplorerUrl: "https://turbochain.io/address/",
	},
	aurora: {
		label: "Aurora",
		icon: "/icons/network/aurora.svg",
		iconDark: "/icons/network/aurora.svg",
		txExplorerUrl: "https://explorer.mainnet.aurora.dev/tx/",
		addressExplorerUrl: "https://explorer.mainnet.aurora.dev/address/",
	},
	aurora_devnet: {
		label: "Aurora Devnet",
		icon: "/icons/network/aurora_devnet.svg",
		iconDark: "/icons/network/aurora_devnet.svg",
		txExplorerUrl: "https://explorer.devnet.aurora.dev/tx/",
		addressExplorerUrl: "https://explorer.devnet.aurora.dev/address/",
	},
	tuxappchain: {
		label: "TuxAppChain",
		icon: "/icons/network/tuxappchain.svg",
		iconDark: "/icons/network/tuxappchain.svg",
		txExplorerUrl: "https://explorer.tuxa.aurora.dev/tx",
		addressExplorerUrl: "https://explorer.tuxa.aurora.dev/address",
	},
	vertex: {
		label: "Vertex",
		icon: "/icons/network/vertex.svg",
		iconDark: "/icons/network/vertex.svg",
	},
	easychain: {
		label: "EasyChain",
		icon: "/icons/network/easychain.svg",
		iconDark: "/icons/network/easychain.svg",
	},
	hako: {
		label: "Hako",
		icon: "/icons/network/hako-light.svg",
		iconDark: "/icons/network/hako-dark.svg",
	},
	qtc: {
		label: "Quantus",
		icon: "/icons/network/quantus.svg",
		iconDark: "/icons/network/quantus.svg",
		txExplorerUrl: "https://explorer.quantus.com/transactions/",
		addressExplorerUrl: "https://explorer.quantus.com/accounts/",
	},
	prl: {
		label: "Pearl",
		icon: "/icons/network/pearl.png",
		iconDark: "/icons/network/pearl.png",
		txExplorerUrl: "https://explorer.pearlresearch.ai/tx/",
		addressExplorerUrl: "https://explorer.pearlresearch.ai/address/",
	},
	optima: {
		label: "Optima",
		icon: "/icons/network/optima.svg",
		iconDark: "/icons/network/optima.svg",
		txExplorerUrl: "https://explorer.optima.aurora.dev/tx/",
		addressExplorerUrl: "https://explorer.optima.aurora.dev/address/",
	},
	hyperliquid: {
		label: "Hyperliquid",
		icon: "/icons/network/hyperliquid.svg",
		iconDark: "/icons/network/hyperliquid.svg",
		txExplorerUrl: "https://app.hyperliquid.xyz/explorer/tx/",
		addressExplorerUrl: "https://app.hyperliquid.xyz/explorer/address/",
	},
	hypercore: {
		label: "HyperCore",
		icon: "/icons/network/hyperliquid.svg",
		iconDark: "/icons/network/hyperliquid.svg",
		txExplorerUrl: "https://hypurrscan.io/evm/tx/",
		addressExplorerUrl: "https://hypurrscan.io/address/",
	},
	hlevm: {
		label: "HyperEVM",
		icon: "/icons/network/hyperliquid.svg",
		iconDark: "/icons/network/hyperliquid.svg",
		txExplorerUrl: "https://hyperevmscan.io/tx/",
		addressExplorerUrl: "https://hyperevmscan.io/address/",
	},
	ltc: {
		label: "Litecoin",
		icon: "/icons/network/litecoin.svg",
		iconDark: "/icons/network/litecoin.svg",
		txExplorerUrl: "https://blockchair.com/litecoin/transaction/",
		addressExplorerUrl: "https://blockchair.com/litecoin/address/",
	},
	litecoin: {
		label: "Litecoin",
		icon: "/icons/network/litecoin.svg",
		iconDark: "/icons/network/litecoin.svg",
		txExplorerUrl: "https://blockchair.com/litecoin/transaction/",
		addressExplorerUrl: "https://blockchair.com/litecoin/address/",
	},
	monad: {
		label: "Monad",
		icon: "/icons/network/monad_white.svg",
		iconDark: "/icons/network/monad.svg",
		txExplorerUrl: "https://monadscan.com/tx/",
		addressExplorerUrl: "https://monadscan.com/address/",
	},
	xlayer: {
		label: "X Layer",
		icon: "/icons/network/xlayer.webp",
		iconDark: "/icons/network/xlayer.webp",
		txExplorerUrl: "https://www.oklink.com/x-layer/tx/",
		addressExplorerUrl: "https://www.oklink.com/x-layer/address/",
	},
	bch: {
		label: "Bitcoin Cash",
		icon: "/icons/network/bch.svg",
		iconDark: "/icons/network/bch.svg",
		txExplorerUrl: "https://www.oklink.com/bch/tx/",
		addressExplorerUrl: "https://www.oklink.com/bch/address/",
	},
	starknet: {
		label: "Starknet",
		icon: "/icons/network/starknet_white.svg",
		iconDark: "/icons/network/starknet.svg",
		txExplorerUrl: "https://starkscan.co/tx/",
		addressExplorerUrl: "https://starkscan.co/contract/",
	},
	adi: {
		label: "ADI",
		icon: "/icons/network/adi.svg",
		iconDark: "/icons/network/adi.svg",
		txExplorerUrl: "https://explorer.adifoundation.ai/tx/",
		addressExplorerUrl: "https://explorer.adifoundation.ai/address/",
	},
	plasma: {
		label: "Plasma",
		icon: "/icons/network/plasma_dark.svg",
		iconDark: "/icons/network/plasma.svg",
		txExplorerUrl: "https://plasmascan.to/tx/",
		addressExplorerUrl: "https://plasmascan.to/address/",
	},
	scroll: {
		label: "Scroll",
		icon: "/icons/network/scroll.svg",
		iconDark: "/icons/network/scroll_dark.svg",
		txExplorerUrl: "https://scrollscan.com/tx/",
		addressExplorerUrl: "https://scrollscan.com/address/",
	},
	aleo: {
		label: "Aleo",
		icon: "/icons/network/aleo-dark.svg",
		iconDark: "/icons/network/aleo-white.svg",
		txExplorerUrl: "https://explorer.provable.com/transaction/",
		addressExplorerUrl: "https://explorer.provable.com/address/",
	},
	movement: {
		label: "Movement",
		icon: "/icons/network/movement_white.svg",
		iconDark: "/icons/network/movement.svg",
		txExplorerUrl: "https://explorer.movementnetwork.xyz/txn/",
		addressExplorerUrl: "https://explorer.movementnetwork.xyz/account/",
	},
	dash: {
		label: "Dash",
		icon: "/icons/network/dash.svg",
		iconDark: "/icons/network/dash.svg",
		txExplorerUrl: "https://blockchair.com/dash/transaction/",
		addressExplorerUrl: "https://blockchair.com/dash/address/",
	},
	abs: {
		label: "Abstract",
		icon: "/icons/network/abs.svg",
		iconDark: "/icons/network/abs.svg",
		txExplorerUrl: "https://abscan.org/tx/",
		addressExplorerUrl: "https://abscan.org/address/",
	},
	fogo: {
		label: "Fogo",
		icon: "/icons/network/fogo.svg",
		iconDark: "/icons/network/fogo.svg",
		txExplorerUrl: "https://fogoscan.com/tx/",
		addressExplorerUrl: "https://fogoscan.com/account/",
	},
} satisfies Record<ChainId, Chain> & Record<string, KnownChain>;

export const KNOWN_CHAINS: Record<string, KnownChain> = knownChains;
